<template>
  <v-row no-gutters class="fill-height flex-column pr-5 pb-5">
    <card-title-row class="pl-0 pt-5" translation-path="sidebar.waterBalanceIwa.title" :has-buttons="false" />
    <water-balance-wizard
      ref="iwa-wizard"
      with-profiles
      use-analysis-date
      tool-name="iwa"
      :inputs="stepsInputs"
      :steps="steps"
      :params-proxy.sync="params"
      :steps-validations="stepsValidations"
      :profiles-data="profilesData"
    >
    </water-balance-wizard>
  </v-row>
</template>

<script>
import WaterBalanceWizard from '@/components/WaterBalanceWizard';
import CardTitleRow from '@/components/CardTitleRow';
import prepareAttributesFeatureCardMixin from '@/mixins/prepareAttributesFeatureCardMixin';
import { get, call } from 'vuex-pathify';

export default {
  name: 'WaterBalanceIwa',
  mixins: [prepareAttributesFeatureCardMixin],
  components: {
    WaterBalanceWizard,
    CardTitleRow,
  },
  data: () => ({
    params: {
      dma_estimate_value: false,
    },
    stepsValidations: {},
  }),
  methods: {
    getIwaProfiles: call(`waterBalance/getIwaProfiles`),
    saveIwaProfile: call(`waterBalance/saveIwaProfile`),
    editIwaProfile: call(`waterBalance/editIwaProfile`),
    deleteIwaProfile: call(`waterBalance/deleteIwaProfile`),
  },
  computed: {
    projectElements: get('layers/project@layers'),
    iwaProfiles: get('waterBalance/iwaProfiles'),
    projectLayers() {
      return this.$_getFlatGroupsLayers(this.projectElements).layers;
    },
    dmaLayers() {
      return this.projectLayers.filter(layer => ['polygon', 'multipolygon'].includes(layer.geometry_type));
    },
    pipesLayers() {
      return this.projectLayers.filter(layer => layer.data_source_name === 'water_pipes');
    },
    terminalsLayers() {
      return this.projectLayers.filter(layer => layer.data_source_name === 'water_terminals');
    },
    watermetersLayers() {
      return this.projectLayers.filter(layer => layer.data_source_name === 'water_watermeters');
    },
    waterPointLayers() {
      return this.projectLayers.filter(
        layer => layer.layer_scope === 'water' && ['point', 'multipoint'].includes(layer.geometry_type)
      );
    },
    defaultInputProps() {
      return {
        dataType: { name: 'select' },
        rules: [v => !!v || [false, 0].includes(v) || this.$i18n.t('rules.required')],
        cols: '7',
        nameCols: '5',
      };
    },
    profilesData() {
      return {
        translationPaths: {
          addProfileDialogTitle: 'dialog.title.profiles.waterBalanceIwaAdd',
          editProfileDialogTitle: 'dialog.title.profiles.waterBalanceIwaEdit',
          deleteProfileDialogTitle: 'dialog.title.profiles.waterBalanceIwaDelete',
        },
        profiles: this.iwaProfiles,
        getProfiles: this.getIwaProfiles,
        saveProfile: this.saveIwaProfile,
        editProfile: this.editIwaProfile,
        deleteProfile: this.deleteIwaProfile,
      };
    },
    steps() {
      return [
        {
          step: 1,
          translationPath: 'sidebar.waterBalanceIwa.flowmeters',
          backDisabled: true,
          nextDisabled: !this.stepsValidations['step1'],
        },
        {
          step: 2,
          translationPath: 'sidebar.waterBalanceIwa.dma',
          backDisabled: this.$refs['iwa-wizard']?.$data.isAnalysisLoading,
          nextDisabled: !this.stepsValidations['step2'] || this.$refs['iwa-wizard']?.$data.isAnalysisLoading,
        },
        {
          step: 3,
          translationPath: 'sidebar.waterBalanceIwa.pipes',
          backDisabled: this.$refs['iwa-wizard']?.$data.isAnalysisLoading,
          nextDisabled: !this.stepsValidations['step3'] || this.$refs['iwa-wizard']?.$data.isAnalysisLoading,
        },
        {
          step: 4,
          translationPath: 'sidebar.waterBalanceIwa.terminals',
          backDisabled: this.$refs['iwa-wizard']?.$data.isAnalysisLoading,
          nextDisabled: !this.stepsValidations['step4'] || this.$refs['iwa-wizard']?.$data.isAnalysisLoading,
        },
        {
          step: 5,
          translationPath: 'sidebar.waterBalanceIwa.watermeters',
          backDisabled: this.$refs['iwa-wizard']?.$data.isAnalysisLoading,
          nextDisabled: !this.stepsValidations['step5'] || this.$refs['iwa-wizard']?.$data.isAnalysisLoading,
        },
        {
          step: 6,
          translationPath: 'sidebar.waterBalanceIwa.analysis',
          backDisabled: this.$refs['iwa-wizard']?.$data.isAnalysisLoading,
          nextDisabled: false,
        },
      ];
    },
    stepsInputs() {
      return [
        {
          name: 'flow_meter_layer',
          isLayer: true,
          step: 1,
          translationPath: 'sidebar.waterBalanceIwa.flowmetersLayer',
          items: this.waterPointLayers,
          itemText: 'name',
          itemValue: 'layer_id',
          disabled: this.waterPointLayers?.length === 0,
          tooltip:
            this.waterPointLayers?.length === 0 ? this.$i18n.t('sidebar.waterBalanceIwa.addFlowmetersToRun') : null,
          ...this.defaultInputProps,
        },
        {
          name: 'flow_meter_name',
          step: 1,
          connectedLayer: 'flow_meter_layer',
          translationPath: 'sidebar.waterBalanceIwa.flowmetersNameAttribute',
          itemText: 'label',
          itemValue: 'attribute',
          disabled: !this.params['flow_meter_layer'],
          ...this.defaultInputProps,
        },
        {
          name: 'flow_meter_scada',
          step: 1,
          connectedLayer: 'flow_meter_layer',
          translationPath: 'sidebar.waterBalanceIwa.scadaAttribute',
          itemText: 'label',
          itemValue: 'attribute',
          disabled: !this.params['flow_meter_layer'],
          ...this.defaultInputProps,
        },
        {
          name: 'dma_layer',
          isLayer: true,
          step: 2,
          translationPath: 'sidebar.waterBalanceIwa.dmaLayer',
          items: this.dmaLayers,
          itemText: 'name',
          itemValue: 'layer_id',
          disabled: this.dmaLayers?.length === 0,
          tooltip: this.dmaLayers?.length === 0 ? this.$i18n.t('sidebar.waterBalanceIwa.addDmaToRun') : null,
          ...this.defaultInputProps,
        },
        {
          name: 'dma_name',
          step: 2,
          connectedLayer: 'dma_layer',
          translationPath: 'sidebar.waterBalanceIwa.dmaNameAttribute',
          itemText: 'label',
          itemValue: 'attribute',
          disabled: !this.params['dma_layer'],
          ...this.defaultInputProps,
        },
        {
          name: 'dma_inflow',
          step: 2,
          connectedLayer: 'dma_layer',
          translationPath: 'sidebar.waterBalanceIwa.dmaPositiveFlowAttribute',
          itemText: 'label',
          itemValue: 'attribute',
          subtitlePath: 'sidebar.waterBalanceIwa.dmaPositiveFlowSubtitle',
          hasSubtitle: true,
          hasSmallSubtitle: true,
          disabled: !this.params['dma_layer'],
          ...this.defaultInputProps,
        },
        {
          name: 'dma_outflow',
          step: 2,
          connectedLayer: 'dma_layer',
          translationPath: 'sidebar.waterBalanceIwa.dmaNegativeFlowAttribute',
          itemText: 'label',
          itemValue: 'attribute',
          subtitlePath: 'sidebar.waterBalanceIwa.dmaNegativeFlowSubtitle',
          hasSubtitle: true,
          hasSmallSubtitle: true,
          disabled: !this.params['dma_layer'],
          ...this.defaultInputProps,
        },
        {
          name: 'dma_population',
          step: 2,
          connectedLayer: 'dma_layer',
          translationPath: 'sidebar.waterBalanceIwa.dmaPopulationAttribute',
          itemText: 'label',
          itemValue: 'attribute',
          disabled: !this.params['dma_layer'] || this.params['dma_estimate_value'],
          ...this.defaultInputProps,
          rules: !this.params['dma_estimate_value']
            ? [v => !!v || [false, 0].includes(v) || this.$i18n.t('rules.required')]
            : [],
        },
        {
          name: 'dma_estimate_value',
          step: 2,
          connectedValue: ['dma_population', 'dma_population_value'],
          isNotAttribute: true,
          skip: true,
          label: this.$i18n.t('sidebar.waterBalanceIwa.dmaEstimatePopulation'),
          dataType: { name: 'checkbox' },
          disabled: !this.params['dma_layer'],
          hasNameColumn: false,
          cols: '12',
        },
        {
          name: 'dma_population_value',
          step: 2,
          translationPath: 'sidebar.waterBalanceIwa.population',
          hidden: !this.params['dma_estimate_value'],
          disabled: !this.params['dma_layer'],
          dataType: { name: 'integer' },
          autocomplete: 'off',
          rules: [v => !!v || [false, 0].includes(v) || this.$i18n.t('rules.required')],
          cols: '7',
          nameCols: '5',
        },
        {
          name: 'pipes_layer',
          isLayer: true,
          step: 3,
          translationPath: 'sidebar.waterBalanceIwa.pipesLayer',
          items: this.pipesLayers,
          itemText: 'name',
          itemValue: 'layer_id',
          disabled: this.pipesLayers?.length === 0,
          tooltip: this.pipesLayers?.length === 0 ? this.$i18n.t('sidebar.waterBalanceIwa.addPipesToRun') : null,
          ...this.defaultInputProps,
        },
        {
          name: 'pipes_analyze',
          step: 3,
          connectedValue: ['pipes_analyze_values'],
          connectedLayer: 'pipes_layer',
          translationPath: 'sidebar.waterBalanceIwa.analysisAttribute',
          itemText: 'label',
          itemValue: 'attribute',
          disabled: !this.params['pipes_layer'],
          ...this.defaultInputProps,
        },
        {
          name: 'pipes_analyze_values',
          step: 3,
          connectedAttribute: 'pipes_analyze',
          translationPath: 'sidebar.waterBalanceIwa.analysisValues',
          multiple: true,
          chips: true,
          autoConvertSelect: false,
          disabled: !this.params['pipes_layer'] || !this.params['pipes_analyze'],
          ...this.defaultInputProps,
        },
        {
          name: 'terminals_layer',
          isLayer: true,
          step: 4,
          translationPath: 'sidebar.waterBalanceIwa.terminalsLayer',
          items: this.terminalsLayers,
          itemText: 'name',
          itemValue: 'layer_id',
          disabled: this.terminalsLayers?.length === 0,
          tooltip:
            this.terminalsLayers?.length === 0 ? this.$i18n.t('sidebar.waterBalanceIwa.addTerminalsToRun') : null,
          ...this.defaultInputProps,
        },
        {
          name: 'terminals_analyze',
          step: 4,
          connectedLayer: 'pipes_layer',
          connectedValue: ['terminals_analyze_value'],
          translationPath: 'sidebar.waterBalanceIwa.analysisAttribute',
          itemText: 'label',
          itemValue: 'attribute',
          disabled: !this.params['terminals_layer'],
          ...this.defaultInputProps,
        },
        {
          name: 'terminals_analyze_value',
          step: 4,
          connectedAttribute: 'terminals_analyze',
          translationPath: 'sidebar.waterBalanceIwa.analysisValue',
          disabled: !this.params['terminals_layer'] || !this.params['terminals_analyze'],
          ...this.defaultInputProps,
        },
        {
          name: 'watermeters_layer',
          isLayer: true,
          step: 5,
          translationPath: 'sidebar.waterBalanceIwa.watermetersLayer',
          items: this.watermetersLayers,
          itemText: 'name',
          itemValue: 'layer_id',
          disabled: this.watermetersLayers?.length === 0,
          tooltip:
            this.watermetersLayers?.length === 0 ? this.$i18n.t('sidebar.waterBalanceIwa.addWatermetersToRun') : null,
          ...this.defaultInputProps,
        },
        {
          name: 'watermeters_daily_usage',
          step: 5,
          connectedLayer: 'watermeters_layer',
          translationPath: 'sidebar.waterBalanceIwa.averageConsumptionAttribute',
          itemText: 'label',
          itemValue: 'attribute',
          disabled: !this.params['watermeters_layer'],
          ...this.defaultInputProps,
        },
        {
          name: 'watermeters_type',
          step: 5,
          connectedLayer: 'watermeters_layer',
          connectedValue: ['watermeters_sold_values', 'watermeters_domestic_values', 'watermeters_internal_values'],
          translationPath: 'sidebar.waterBalanceIwa.typeAttribute',
          itemText: 'label',
          itemValue: 'attribute',
          disabled: !this.params['watermeters_layer'],
          ...this.defaultInputProps,
        },
        {
          name: 'watermeters_sold_values',
          step: 5,
          connectedAttribute: 'watermeters_type',
          translationPath: 'sidebar.waterBalanceIwa.soldWaterValue',
          multiple: true,
          chips: true,
          autoConvertSelect: false,
          disabled: !this.params['watermeters_layer'] || !this.params['watermeters_type'],
          ...this.defaultInputProps,
        },
        {
          name: 'watermeters_domestic_values',
          step: 5,
          connectedAttribute: 'watermeters_type',
          translationPath: 'sidebar.waterBalanceIwa.houseTypeValue',
          multiple: true,
          chips: true,
          autoConvertSelect: false,
          disabled: !this.params['watermeters_layer'] || !this.params['watermeters_type'],
          ...this.defaultInputProps,
        },
        {
          name: 'watermeters_internal_values',
          step: 5,
          connectedAttribute: 'watermeters_type',
          translationPath: 'sidebar.waterBalanceIwa.ownPurposeValue',
          multiple: true,
          chips: true,
          autoConvertSelect: false,
          disabled: !this.params['watermeters_layer'] || !this.params['watermeters_type'],
          ...this.defaultInputProps,
        },
      ];
    },
  },
};
</script>

<style lang="scss" scoped>
::v-deep .wizard-form .row {
  padding-right: 0 !important;
}
::v-deep {
  .v-select__selections {
    flex-wrap: wrap !important;
  }
  .v-alert--text:before {
    opacity: 0;
  }
  .v-stepper--alt-labels .v-stepper__header .v-stepper__step[data-v-1a33ef5e] {
    min-width: 90px !important;
  }
  .v-input--checkbox {
    margin-top: 0 !important;
    .v-messages__wrapper {
      font-family: Roboto;
      font-style: italic;
      font-weight: 300;
      font-size: 11px;
      padding-left: 34px;
      color: rgba(0, 0, 0, 0.59);
    }
    .v-label {
      font-family: Roboto;
      font-style: normal;
      font-weight: normal;
      font-size: 14px;
      text-align: left;
      line-height: 16px;
      color: #202124;
    }
  }
}
</style>
